import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Popconfirm,
  message,
  Spin,
  Row,
  Col,
  Statistic,
  Drawer,
  Tabs,
  Descriptions,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  DatabaseOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { CompanyInstanceService } from '../../services/api/companyInstanceService';
import { CompanyService } from '../../services/api/companyService';
import { UserService } from '../../services/api/userService';
import {
  CompanyInstance,
  CompanyInstanceCreate,
  CompanyInstanceUpdate,
  CompanyInstanceFilters,
} from '../../types/companyInstance';
import { Company } from '../../types/company';
import { User } from '../../services/api/userService';
import styles from './CompanyInstanceManagement.module.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

interface CompanyInstanceManagementProps {
  className?: string;
}

export const CompanyInstanceManagement: React.FC<CompanyInstanceManagementProps> = ({
  className,
}) => {
  const [instances, setInstances] = useState<CompanyInstance[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailDrawerVisible, setIsDetailDrawerVisible] = useState(false);
  const [editingInstance, setEditingInstance] = useState<CompanyInstance | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<CompanyInstance | null>(null);
  const [instanceArtifacts, setInstanceArtifacts] = useState<any[]>([]);
  const [instanceUsers, setInstanceUsers] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<CompanyInstanceFilters>({});

  useEffect(() => {
    loadInstances();
    loadCompanies();
    loadUsers();
  }, [filters]);

  const loadInstances = async () => {
    setLoading(true);
    try {
      const instanceList = await CompanyInstanceService.getAll(filters);
      setInstances(instanceList);
    } catch (err: any) {
      message.error(err.message || 'Failed to load user-company links');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const companyList = await CompanyService.getAll();
      setCompanies(companyList);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const userList = await UserService.getAll();
      setUsers(userList);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadInstanceDetailData = async (instanceId: string) => {
    try {
      const [artifacts, instanceUsersData] = await Promise.all([
        CompanyInstanceService.getArtifacts(instanceId),
        CompanyInstanceService.getUsers(instanceId),
      ]);
      setInstanceArtifacts(artifacts);
      setInstanceUsers(instanceUsersData);
    } catch (error) {
      console.error('Error loading instance detail data:', error);
    }
  };

  const handleCreateInstance = () => {
    setEditingInstance(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditInstance = (instance: CompanyInstance) => {
    setEditingInstance(instance);
    form.setFieldsValue({
      company_id: instance.company_id,
      user_id: instance.user_id,
      instance_name: instance.instance_name,
      is_active: instance.is_active,
    });
    setIsModalVisible(true);
  };

  const handleDeleteInstance = async (instanceId: string) => {
    try {
      await CompanyInstanceService.delete(instanceId);
      message.success('User-company link deleted');
      await loadInstances();
    } catch (err: any) {
      message.error(err.message || 'Failed to delete user-company link');
    }
  };

  const handleViewInstance = async (instance: CompanyInstance) => {
    setSelectedInstance(instance);
    setIsDetailDrawerVisible(true);
    await loadInstanceDetailData(instance.id);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingInstance) {
        const updateData: CompanyInstanceUpdate = {
          instance_name: values.instance_name,
          is_active: values.is_active,
        };
        await CompanyInstanceService.update(editingInstance.id, updateData);
        message.success('User-company link updated');
      } else {
        const createData: CompanyInstanceCreate = {
          company_id: values.company_id,
          user_id: values.user_id,
          instance_name: values.instance_name,
          is_active: values.is_active !== undefined ? values.is_active : true,
        };
        await CompanyInstanceService.create(createData);
        message.success('User-company link created');
      }

      setIsModalVisible(false);
      await loadInstances();
    } catch (err: any) {
      message.error(err.message || 'Failed to save user-company link');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingInstance(null);
    form.resetFields();
  };

  const handleFilterChange = (key: keyof CompanyInstanceFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const filteredInstances = instances.filter(instance => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const company = companies.find(c => c.id === instance.company_id);
    const user = users.find(u => u.id === instance.user_id);
    return (
      instance.instance_name.toLowerCase().includes(searchLower) ||
      company?.name.toLowerCase().includes(searchLower) ||
      user?.username.toLowerCase().includes(searchLower) ||
      user?.email.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    {
      title: 'Link name',
      dataIndex: 'instance_name',
      key: 'instance_name',
      render: (text: string, instance: CompanyInstance) => (
        <Button
          type="link"
          onClick={() => handleViewInstance(instance)}
          style={{ padding: 0 }}
        >
          <Text strong>{text}</Text>
        </Button>
      ),
      sorter: (a: CompanyInstance, b: CompanyInstance) =>
        a.instance_name.localeCompare(b.instance_name),
    },
    {
      title: 'Company',
      key: 'company',
      render: (instance: CompanyInstance) => {
        const company = companies.find(c => c.id === instance.company_id);
        return company ? <Text>{company.name}</Text> : <Text type="secondary">{instance.company_id}</Text>;
      },
    },
    {
      title: 'User',
      key: 'user',
      render: (instance: CompanyInstance) => {
        const user = users.find(u => u.id === instance.user_id);
        return user ? <Text>{user.username || user.email}</Text> : <Text type="secondary">{instance.user_id}</Text>;
      },
    },
    {
      title: 'Role',
      key: 'role',
      render: (instance: CompanyInstance) => {
        const user = users.find(u => u.id === instance.user_id);
        const role = user?.role;
        return role ? <Tag>{role}</Tag> : <Text type="secondary">No role</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (instance: CompanyInstance) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditInstance(instance)}
            title="Edit instance"
          />
          <Popconfirm
            title="Are you sure you want to delete this instance?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteInstance(instance.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete instance"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const activeInstances = instances.filter(instance => instance.is_active).length;
  const totalInstances = instances.length;

  return (
    <div className={`${styles.companyInstanceManagement} ${className || ''}`}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>User-company links</Title>
        <Text type="secondary">Memberships that attach a user to a company. This is not a list of companies.</Text>
      </div>

      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total links"
              value={totalInstances}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Active links"
              value={activeInstances}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Inactive links"
              value={totalInstances - activeInstances}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search instances..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: '300px' }}
            allowClear
          />
          <Select
            placeholder="Filter by Company"
            style={{ width: '200px' }}
            allowClear
            onChange={(value) => handleFilterChange('company_id', value)}
            value={filters.company_id}
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {companies.map(company => (
              <Option key={company.id} value={company.id}>
                {company.name}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Filter by User"
            style={{ width: '200px' }}
            allowClear
            onChange={(value) => handleFilterChange('user_id', value)}
            value={filters.user_id}
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
            }
          >
            {users.map(user => (
              <Option key={user.id} value={user.id}>
                {user.username || user.email}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Filter by Status"
            style={{ width: '150px' }}
            allowClear
            onChange={(value) => handleFilterChange('active_only', value === true ? true : value === false ? false : undefined)}
            value={filters.active_only}
          >
            <Option value={true}>Active Only</Option>
            <Option value={false}>Inactive Only</Option>
          </Select>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateInstance}
          >
            Create link
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={filteredInstances}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} instances`,
          }}
        />
      </Card>

      <Modal
        title={editingInstance ? 'Edit user-company link' : 'Create user-company link'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form form={form} layout="vertical">
          {!editingInstance && (
            <>
              <Form.Item
                name="company_id"
                label="Company"
                rules={[{ required: true, message: 'Please select a company' }]}
              >
                <Select placeholder="Select company" showSearch filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }>
                  {companies.map(company => (
                    <Option key={company.id} value={company.id}>
                      {company.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="user_id"
                label="User"
                rules={[{ required: true, message: 'Please select a user' }]}
              >
                <Select placeholder="Select user" showSearch filterOption={(input, option) =>
                  (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                }>
                  {users.map(user => (
                    <Option key={user.id} value={user.id}>
                      {user.username || user.email}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
          <Form.Item
            name="instance_name"
            label="Link name"
            rules={[{ required: true, message: 'Please enter a link name' }]}
          >
            <Input placeholder="Label for this user-company membership" />
          </Form.Item>
          <Form.Item name="is_active" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={<Title level={4}>{selectedInstance?.instance_name || 'Link details'}</Title>}
        placement="right"
        width={800}
        onClose={() => {
          setIsDetailDrawerVisible(false);
          setSelectedInstance(null);
        }}
        open={isDetailDrawerVisible}
      >
        {selectedInstance && (
          <Tabs defaultActiveKey="overview">
            <TabPane
              tab={
                <span>
                  <DatabaseOutlined />
                  Overview
                </span>
              }
              key="overview"
            >
              <Descriptions bordered column={1} style={{ marginBottom: '24px' }}>
                <Descriptions.Item label="Link name">{selectedInstance.instance_name}</Descriptions.Item>
                <Descriptions.Item label="Company">
                  {companies.find(c => c.id === selectedInstance.company_id)?.name || selectedInstance.company_id}
                </Descriptions.Item>
                <Descriptions.Item label="User">
                  {users.find(u => u.id === selectedInstance.user_id)?.username || users.find(u => u.id === selectedInstance.user_id)?.email || selectedInstance.user_id}
                </Descriptions.Item>
                <Descriptions.Item label="Role">
                  {selectedInstance.role ? <Tag>{selectedInstance.role}</Tag> : <Text type="secondary">No role</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={selectedInstance.is_active ? 'green' : 'red'}>
                    {selectedInstance.is_active ? 'Active' : 'Inactive'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {new Date(selectedInstance.created_at).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Updated">
                  {new Date(selectedInstance.updated_at).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <DatabaseOutlined />
                  Artifacts ({instanceArtifacts.length})
                </span>
              }
              key="artifacts"
            >
              <Table
                columns={[
                  { 
                    title: 'Name', 
                    dataIndex: 'name', 
                    key: 'name',
                    sorter: (a: any, b: any) => (a.name || '').localeCompare(b.name || ''),
                    sortDirections: ['ascend', 'descend']
                  },
                  { 
                    title: 'Type', 
                    dataIndex: 'type', 
                    key: 'type',
                    sorter: (a: any, b: any) => (a.type || '').localeCompare(b.type || ''),
                    sortDirections: ['ascend', 'descend']
                  },
                  { 
                    title: 'Status', 
                    dataIndex: 'status', 
                    key: 'status',
                    sorter: (a: any, b: any) => (a.status || '').localeCompare(b.status || ''),
                    sortDirections: ['ascend', 'descend']
                  },
                ]}
                dataSource={instanceArtifacts}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <TeamOutlined />
                  Users ({instanceUsers.length})
                </span>
              }
              key="users"
            >
              <Table
                columns={[
                  { title: 'User', dataIndex: 'username', key: 'username' },
                  { title: 'Email', dataIndex: 'email', key: 'email' },
                  { title: 'Role', dataIndex: 'role', key: 'role', render: (role: string) => role ? <Tag>{role}</Tag> : <Text type="secondary">No role</Text> },
                ]}
                dataSource={instanceUsers}
                rowKey="id"
                pagination={{ pageSize: 10 }}
              />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <BarChartOutlined />
                  Analytics
                </span>
              }
              key="analytics"
            >
              <Card>
                <Text type="secondary">Analytics coming soon</Text>
              </Card>
            </TabPane>
          </Tabs>
        )}
      </Drawer>
    </div>
  );
};
